import React, { useState } from "react";
import { ArrowUpRight, Globe, Mail, MapPin, Phone } from "lucide-react";
import PageHeader from "../components/PageHeader";
import StudioMap from "../components/StudioMap";
import MapErrorBoundary from "../components/MapErrorBoundary";
import Reveal from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import LogoSlider from "../components/LogoSlider";
import Seo from "../components/Seo";
import { PRODUCT_LINES_L } from "../content";
import { SEO_META } from "../seo";
import { api } from "../adminApi";
import { useI18n } from "../i18n";
import { useSiteData } from "../siteData";

const EMPTY = { name: "", email: "", company: "", system: "", area: "", message: "" };
const FIELD =
  "rounded-none border-[#E2E0D8] bg-[#F7F6F2] h-12 text-[15px] focus-visible:ring-1 focus-visible:ring-[#C8B29B]";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  const { t, loc, lang } = useI18n();
  const { collections, content } = useSiteData();
  const offices = content.contact?.offices || [];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast({ title: t("toast.missingTitle"), description: t("toast.missingText") });
      return;
    }
    setSending(true);
    try {
      await api.sendEnquiry({ ...form, lang });
      setForm(EMPTY);
      toast({ title: t("toast.sentTitle"), description: t("toast.sentText") });
    } catch (err) {
      toast({ title: t("toast.missingTitle"), description: err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <Seo path="/contact" title={loc(SEO_META.contact.title)} description={loc(SEO_META.contact.description)} />
      <PageHeader
        index={loc(content.pages?.contactIndex) || "[ 06 // CONTACT ]"}
        title={<span className="whitespace-pre-line">{loc(content.pages?.contactTitle) || t("contact.title")}</span>}
        lead={loc(content.pages?.contactLead) || t("contact.lead")}
      />

      <section className="shell py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-[#FFFFFF] border border-[#E2E0D8] p-7 lg:p-10" data-testid="contact-form">
              <p className="mono text-[9.5px] text-[#6B675F] mb-8">{loc(content.pages?.contactFormLabel) || t("contact.enquiry")}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="mono text-[9px] text-[#6B675F]">
                    {t("contact.name")} *
                  </Label>
                  <Input id="name" data-testid="contact-name" value={form.name} onChange={set("name")} className={FIELD} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="mono text-[9px] text-[#6B675F]">
                    {t("contact.email")} *
                  </Label>
                  <Input id="email" data-testid="contact-email" type="email" value={form.email} onChange={set("email")} className={FIELD} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company" className="mono text-[9px] text-[#6B675F]">
                    {t("contact.company")}
                  </Label>
                  <Input id="company" data-testid="contact-company" value={form.company} onChange={set("company")} className={FIELD} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="area" className="mono text-[9px] text-[#6B675F]">
                    {t("contact.area")}
                  </Label>
                  <Input id="area" data-testid="contact-area" value={form.area} onChange={set("area")} className={FIELD} />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="mono text-[9px] text-[#6B675F]">{t("contact.system")}</Label>
                  <Select value={form.system} onValueChange={(v) => setForm((f) => ({ ...f, system: v }))}>
                    <SelectTrigger className={FIELD} data-testid="contact-system">
                      <SelectValue placeholder={t("contact.selectSystem")} />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-[#E2E0D8]">
                      {collections
                        .filter((c) => c.key !== "match")
                        .map((c) => (
                          <SelectItem key={c.id} value={`MATCH ${c.no} ${c.name}`}>
                            MATCH {c.no} · {c.name}
                          </SelectItem>
                        ))}
                      {PRODUCT_LINES_L.slice(1).map((p) => (
                        <SelectItem key={p.id} value={p.name}>
                          {p.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="Custom">{t("contact.custom")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="message" className="mono text-[9px] text-[#6B675F]">
                    {t("contact.message")} *
                  </Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message"
                    rows={6}
                    value={form.message}
                    onChange={set("message")}
                    className="rounded-none border-[#E2E0D8] bg-[#F7F6F2] text-[15px] focus-visible:ring-1 focus-visible:ring-[#C8B29B]"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit"
                className="mt-9 inline-flex items-center gap-2 bg-[#141414] text-[#F7F6F2] px-7 py-4 mono text-[10px] hover:bg-[#2A2A28] disabled:opacity-60 transition-colors duration-300"
              >
                {sending ? t("cta.sending") : t("cta.send")} <ArrowUpRight size={14} strokeWidth={1.6} />
              </button>
            </form>
          </div>

          <div className="lg:col-span-4 lg:col-start-9">
            {offices.map((o, i) => (
              <Reveal key={o.country} delay={i * 100}>
                <div className="border-t border-[#E2E0D8] py-8">
                  <p className="mono text-[9.5px] text-[#6B675F] mb-5">
                    {o.country} {t("contact.office")}
                  </p>
                  <h3 className="text-[18px] font-medium text-[#141414] leading-snug">{o.name}</h3>
                  <div className="mt-5 space-y-3">
                    <p className="flex items-start gap-3 text-[14px] text-[#141414]/70">
                      <MapPin size={14} strokeWidth={1.5} className="mt-0.5 shrink-0 text-[#C8B29B]" />
                      <span>{loc(o.city) || o.address.join(", ")}</span>
                    </p>
                    {o.phones.map((p) => (
                      <a
                        key={p}
                        href={`tel:${p.replace(/\s/g, "")}`}
                        className="flex items-center gap-3 text-[14px] text-[#141414]/80 hover:text-[#141414] transition-colors"
                      >
                        <Phone size={14} strokeWidth={1.5} className="shrink-0 text-[#C8B29B]" />
                        <span>{p}</span>
                        {o.phoneNote && <span className="mono text-[8px] text-[#8C7862]">({o.phoneNote})</span>}
                      </a>
                    ))}
                    <a href={`mailto:${o.email}`} className="flex items-center gap-3 text-[14px] text-[#141414] transition-colors">
                      <Mail size={14} strokeWidth={1.5} className="shrink-0 text-[#C8B29B]" />
                      <span className="link-underline">{o.email}</span>
                    </a>
                    <a
                      href={o.website}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`office-website-${o.country}`}
                      className="flex items-center gap-3 text-[14px] text-[#141414] transition-colors"
                    >
                      <Globe size={14} strokeWidth={1.5} className="shrink-0 text-[#C8B29B]" />
                      <span className="link-underline">{o.websiteLabel}</span>
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}

            <div className="border-t border-[#E2E0D8] py-8">
              <p className="mono text-[9.5px] text-[#6B675F] mb-4">{loc(content.pages?.contactHoursLabel) || t("contact.hours")}</p>
              <p className="text-[14px] text-[#141414]/70">{loc(content.contact?.hours) || t("contact.hoursValue")}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#EFECE6] border-t border-[#E2E0D8] py-16 lg:py-20">
        <div className="shell">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
            <div>
              <p className="mono text-[9.5px] text-[#6B675F] mb-4">{loc(content.pages?.contactLocationsLabel) || t("contact.locations")}</p>
              <h2 className="display text-[28px] sm:text-[38px] lg:text-[46px] text-[#141414] leading-[1.04] whitespace-pre-line">
                {loc(content.pages?.contactMapTitle) || t("contact.twoStudios")}
              </h2>
            </div>
            <p className="mono text-[9px] text-[#6B675F] sm:text-right max-w-[280px] leading-relaxed">
              {loc(content.pages?.contactMapNote) || t("contact.mapNote")}
            </p>
          </div>
          <MapErrorBoundary>
            <StudioMap />
          </MapErrorBoundary>
        </div>
      </section>
      <LogoSlider page="contact" anchor="bottom" />
    </div>
  );
}
